import React, { useCallback } from 'react';
import { Joyride, STATUS, type EventData, type Step } from 'react-joyride';
import { useTour } from '../../hooks/useTour';
import { useTourStore } from '../../stores/tourStore';
import { onboardingApi } from '../../services/onboardingApi';
import type { TourStep } from '../../types/tour';

interface Props {
  children: React.ReactNode;
}

/**
 * TourStep (内部型) を react-joyride v3 の Step 型に変換する.
 * - disableBeacon → skipBeacon (v3 で改名)
 * - spotlightClicks は v3 では blockTargetInteraction: false (デフォルト) で等価なため除去
 */
function toJoyrideStep(tourStep: TourStep): Step {
  // v3 に存在しないプロパティを除外して変換
  const { disableBeacon, spotlightClicks: _unused, ...rest } = tourStep;
  void _unused; // intentionally unused — v3 では blockTargetInteraction: false (デフォルト) で等価
  return {
    ...rest,
    ...(disableBeacon !== undefined ? { skipBeacon: disableBeacon } : {}),
  };
}

/**
 * react-joyride をラップし、tourStore の activeTourKey を監視して
 * ツアーを表示する Provider. App ツリーの最上位 (BrowserRouter 内) に配置する.
 */
export function TourProvider({ children }: Props) {
  const { activeTour } = useTour();
  const completeTour = useTourStore((s) => s.completeTour);
  const skipTour = useTourStore((s) => s.skipTour);

  const handleCallback = useCallback(
    (data: EventData) => {
      if (!activeTour) return;
      const { status } = data;
      if (status === STATUS.FINISHED) {
        completeTour(activeTour.key);
        // Best-effort: サーバー側 visited_features にも追加 (失敗は無視)
        onboardingApi.recordEvent('tour_completed', undefined, { key: activeTour.key }).catch(() => {});
      } else if (status === STATUS.SKIPPED) {
        skipTour(activeTour.key);
        onboardingApi.recordEvent('tour_skipped', undefined, { key: activeTour.key }).catch(() => {});
      }
    },
    [activeTour, completeTour, skipTour],
  );

  return (
    <>
      {children}
      {activeTour && (
        <Joyride
          run={!!activeTour}
          steps={activeTour.steps.map(toJoyrideStep)}
          continuous
          onEvent={handleCallback}
          locale={{
            back: '戻る',
            close: '閉じる',
            last: '完了',
            next: '次へ',
            skip: 'スキップ',
          }}
          options={{
            zIndex: 10000,
            primaryColor: '#1890ff',
            // v2 の showSkipButton=true 相当: skip ボタンを buttons 配列に含める
            buttons: ['back', 'close', 'primary', 'skip'],
            // v2 の showProgress=true 相当
            showProgress: true,
          }}
        />
      )}
    </>
  );
}
